
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Share, Plus, MoreVertical, Download } from 'lucide-react';

interface BrowserInfo {
  name: string;
  isIOS: boolean;
  isAndroid: boolean;
  canInstall: boolean;
  isSafari: boolean;
  isChrome: boolean;
}

export function BrowserInstructions() {
  const { t } = useTranslation();
  const [browserInfo, setBrowserInfo] = useState<BrowserInfo | null>(null);

  useEffect(() => {
    const detectBrowser = () => {
      const userAgent = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const isAndroid = /Android/.test(userAgent);
      const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(userAgent);
      const isChrome = /Chrome|CriOS/.test(userAgent);
      const isFirefox = /Firefox|FxiOS/.test(userAgent);
      const isEdge = /EdgiOS|Edge/.test(userAgent);
      
      const iosVersionMatch = userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
      const iosVersion = iosVersionMatch ? parseInt(iosVersionMatch[1] || '0') : 0;
      
      let name = 'Browser';
      let canInstall = true;

      if (isIOS && isSafari) {
        name = iosVersion >= 16 ? 'Safari iOS (modern)' : 'Safari iOS';
      } else if (isIOS && isChrome) {
        name = 'Chrome iOS';
      } else if (isAndroid && isChrome) {
        name = 'Chrome Android';
      } else if (isChrome) {
        name = 'Chrome';
      } else if (isFirefox) {
        name = 'Firefox';
      } else if (isEdge) {
        name = 'Edge';
      }

      setBrowserInfo({ name, isIOS, isAndroid, canInstall, isSafari, isChrome });
    };

    detectBrowser();
  }, []);

  if (!browserInfo?.canInstall) {
    return null;
  }

  return (
    <Card className="w-full">
      <CardContent className="p-4 space-y-3">
        <h3 className="font-medium text-sm text-center">{t('common.pwa.howToInstall', "Comment installer l'application :")}</h3>
        
        {browserInfo.isIOS ? (
          <div className="space-y-4 text-xs text-muted-foreground">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
              <p className="font-medium text-sm mb-2">📱 {t('common.pwa.iosInstall', 'Installation sur Safari iOS')}</p>
              <p className="text-xs">{t('common.pwa.iosTroubleshoot', 'Si les boutons ne répondent pas, essayez ces solutions :')}</p>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="bg-primary/10 text-primary rounded-full w-6 h-6 flex items-center justify-center font-medium text-xs flex-shrink-0 mt-0.5">1</span>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span>{t('common.pwa.tapShare', 'Appuyez sur le bouton Partager')}</span>
                    <Share className="h-4 w-4 text-blue-500" />
                  </div>
                  <p className="text-xs text-amber-600">⚠️ {t('common.pwa.bottomSafari', "En bas de l'écran dans Safari")}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="bg-primary/10 text-primary rounded-full w-6 h-6 flex items-center justify-center font-medium text-xs flex-shrink-0 mt-0.5">2</span>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span>{t('common.pwa.addHomeScreen', 'Faites défiler et sélectionnez "Sur l\'écran d\'accueil"')}</span>
                    <Plus className="h-4 w-4 text-blue-500" />
                  </div>
                  <p className="text-xs text-amber-600">⚠️ {t('common.pwa.scrollDown', 'Faites défiler vers le bas dans la liste des options')}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="bg-primary/10 text-primary rounded-full w-6 h-6 flex items-center justify-center font-medium text-xs flex-shrink-0 mt-0.5">3</span>
                <span>{t('common.pwa.tapAdd', 'Appuyez sur "Ajouter" pour confirmer')}</span>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 space-y-2">
              <p className="font-medium text-sm">🔧 {t('common.pwa.stillNotWorking', 'Si ça ne fonctionne toujours pas :')}</p>
              <ul className="text-xs space-y-1 ml-3">
                <li>• {t('common.pwa.refreshPage', 'Actualisez la page (glissez vers le bas)')}</li>
                <li>• {t('common.pwa.restartSafari', 'Redémarrez Safari complètement')}</li>
                <li>• {t('common.pwa.checkSafari', 'Vérifiez que vous êtes sur Safari (pas Chrome iOS)')}</li>
                <li>• {t('common.pwa.tryPrivate', 'Essayez en mode navigation privée')}</li>
              </ul>
            </div>
            
            <div className="text-center text-xs text-primary bg-primary/5 p-3 rounded-md">
              ✅ {t('common.pwa.iconWillAppear', "L'icône Sclepios apparaîtra sur votre écran d'accueil !")}
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-xs text-muted-foreground">
            <div className="flex items-start gap-3">
              <span className="bg-primary/10 text-primary rounded-full w-6 h-6 flex items-center justify-center font-medium text-xs flex-shrink-0 mt-0.5">1</span>
              <div className="flex items-center gap-2">
                <span>{t('common.pwa.tapMenu', 'Appuyez sur le menu (3 points)')}</span>
                <MoreVertical className="h-4 w-4" />
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="bg-primary/10 text-primary rounded-full w-6 h-6 flex items-center justify-center font-medium text-xs flex-shrink-0 mt-0.5">2</span>
              <div className="flex items-center gap-2">
                <span>{t('common.pwa.selectInstall', 'Sélectionnez "Installer l\'application" ou "Ajouter à l\'écran d\'accueil"')}</span>
                <Download className="h-4 w-4 text-green-500" />
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="bg-primary/10 text-primary rounded-full w-6 h-6 flex items-center justify-center font-medium text-xs flex-shrink-0 mt-0.5">3</span>
              <span>{t('common.pwa.confirmInstall', "Confirmez l'installation")}</span>
            </div>
            <div className="text-center text-xs text-primary bg-primary/5 p-2 rounded-md mt-3">
              {t('common.pwa.quickAccess', "Accès rapide depuis votre écran d'accueil !")}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
